/**
 * HOME-01 fix round 1 — the property-type taxonomy, in ONE place.
 *
 * WHY THIS FILE EXISTS
 * The chips shipped in the redesign were an explicit placeholder: five labels
 * whose `param` was a `PropertyType` enum member. Two things were wrong with
 * that, and both were invisible until the counts were checked against the
 * database.
 *
 *   1. "House / Villa" resolved to Villa alone, so the 13 bungalows in
 *      `listings` and the 13 in the index were unreachable from any chip.
 *      "Land" resolved to Plot and lost nothing, but only by luck.
 *   2. A chip carried ONE value, and the chip the product actually wants
 *      ("House & Villa") covers TWO `property_type` values. A single-value
 *      filter cannot express it, so the taxonomy had to grow a plural before
 *      the labels could be corrected.
 *
 * THE VALUES ARE THE DATABASE'S, VERIFIED (prod SQL, 2026-08-14). Both tables
 * carry the same six-word vocabulary in `property_type text`:
 *
 *   property_type    | listings | syndicated_listings
 *   -----------------|----------|--------------------
 *   apartment        |       51 |                 843
 *   plot             |        9 |                 349
 *   villa            |       15 |                 226
 *   townhouse        |       17 |                  78
 *   new_development  |        4 |                  73
 *   bungalow         |        4 |                  13
 *
 * Every value below appears in that table and nowhere else. Nothing here is
 * derived from the `PropertyType` enum in types.ts: that enum is the UI's
 * spelling ('New Development'), and the round trip through
 * `.toLowerCase().replace(/\s+/g, '_')` is exactly the kind of implicit
 * contract that let "House / Villa" quietly mean one thing.
 *
 * SALE ONLY. There is no rental vocabulary in this product and none may be
 * added here — no `property_type` value distinguishes a let, and no column
 * anywhere in either table records one.
 */

/** A `property_type` value as both tables store it. */
export type DbPropertyType =
  | 'apartment'
  | 'villa'
  | 'bungalow'
  | 'townhouse'
  | 'plot'
  | 'new_development';

export interface HomeTypeChip {
  /** Stable id. What the URL carries and what the active-chip state holds. */
  id: string;
  /** The label on the chip. */
  label: string;
  /**
   * The `property_type` values this chip selects, for BOTH `listings` and
   * `syndicated_public`. Empty means "no filter" — the All types chip.
   */
  values: readonly DbPropertyType[];
}

/**
 * The six chips, in the design's 3×2 order.
 *
 * This array is the only place the mapping exists. The hero chips, the filter
 * sheet's property-type grid, the marketplace query and the syndicated query
 * all read it, so a chip cannot mean one set of rows in the UI and another in
 * the query — which is precisely how the placeholder set went wrong.
 */
export const HOME_TYPE_CHIPS: readonly HomeTypeChip[] = [
  { id: 'all', label: 'All types', values: [] },
  { id: 'apartment', label: 'Apartment', values: ['apartment'] },
  { id: 'house_villa', label: 'House & Villa', values: ['villa', 'bungalow'] },
  { id: 'townhouse', label: 'Townhouse', values: ['townhouse'] },
  { id: 'plot_land', label: 'Plot & Land', values: ['plot'] },
  { id: 'new_builds', label: 'New builds', values: ['new_development'] },
] as const;

/** The id of the unfiltered chip. */
export const ALL_TYPES_CHIP_ID = 'all';

export function chipById(id: string | null | undefined): HomeTypeChip {
  return HOME_TYPE_CHIPS.find((c) => c.id === id) ?? HOME_TYPE_CHIPS[0];
}

export function chipIndexById(id: string | null | undefined): number {
  const index = HOME_TYPE_CHIPS.findIndex((c) => c.id === id);
  return index === -1 ? 0 : index;
}

/**
 * The `property_type` values a chip selects, or `undefined` for All types.
 *
 * `undefined` rather than `[]` on purpose: a query builder handed an empty
 * array has to decide whether that means "no filter" or "match nothing", and
 * every builder deciding for itself is a bug waiting for a caller who guesses
 * differently.
 */
export function dbTypesForChip(id: string | null | undefined): DbPropertyType[] | undefined {
  const values = chipById(id).values;
  return values.length > 0 ? [...values] : undefined;
}

export function isAllTypes(id: string | null | undefined): boolean {
  return chipById(id).id === ALL_TYPES_CHIP_ID;
}

/**
 * Legacy `?type=` values → chip id.
 *
 * Links already in the wild (and every saved search stored before this round)
 * carry the `PropertyType` spelling — `type=Villa`, `type=New Development`.
 * Those must keep landing on a sensible chip rather than silently falling back
 * to All types, which would show a buyer a filter they did not ask for.
 * `Villa` and `Bungalow` both resolve to House & Villa, which widens the old
 * link's result set by design: that widening IS the fix.
 */
const LEGACY_TYPE_TO_CHIP: Record<string, string> = {
  villa: 'house_villa',
  bungalow: 'house_villa',
  apartment: 'apartment',
  townhouse: 'townhouse',
  plot: 'plot_land',
  land: 'plot_land',
  new_development: 'new_builds',
  project: 'new_builds',
};

/**
 * Read a `?type=` parameter, accepting both the chip ids this round writes and
 * the `PropertyType` spellings written before it. Unknown values resolve to
 * All types — the same fallback `parseActiveType` already used.
 */
export function chipIdFromParam(raw: string | null | undefined): string {
  if (!raw) return ALL_TYPES_CHIP_ID;
  const direct = HOME_TYPE_CHIPS.find((c) => c.id === raw);
  if (direct) return direct.id;
  const normalised = raw.trim().toLowerCase().replace(/[\s/&-]+/g, '_');
  return LEGACY_TYPE_TO_CHIP[normalised] ?? ALL_TYPES_CHIP_ID;
}
